import React, { useState, useEffect, useMemo } from 'react';
import { InputAdornment, TextField, Grid, Tooltip, Popover, Typography, Autocomplete, Button, IconButton, Menu, MenuItem, Box, FormControl, FormControlLabel, RadioGroup, Radio, FormLabel, InputLabel, Select } from '@mui/material';
import { IoIosSearch } from "react-icons/io";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { GoTrash } from "react-icons/go";
import { browserHistory } from 'react-router';
import { AppProperties } from '../../common/function';
import ApiRequestService from '../../component/ApiRequestService';
import { NotificationManager } from 'react-notifications';
import { getRandomColorForSubjectMatrix, getRandomColorForTopicMatrix, generateLabelForName } from '../../component/vectors/commonHelpers';
import NewAssignmentModal from '../AssignmentMatrix/Modal/NewAssignmentModal';
import AssignmentDetailModal from '../AssignmentMatrix/Modal/AssignmentDetailModal';
import CategoryModal from '../AssignmentMatrix/Modal/CategoryModal';
import DeleteDialog from '../DeleteDialog';
import { DEFAULT_MASTERY_COUNT } from '../AssignmentMatrix/Constants';
import DownloadMatrixCSV from '../AssignmentMatrix/DownloadMatrixCSV';
import "../AssignmentMatrix/style.css";
import "./style.css";
import SFTooltip from '../AssignmentMatrix/components/SFTooltip';
import { DeletedCategoriesDialog, hasValidLibraryIds, isAMLNotShared, READ_ONLY_HINT, SYNC_NOTIFICATION_MS, UnsyncedCategoriesDialog } from './LibrarySync';
import { FaExclamation } from "react-icons/fa6";
import { TbTargetArrow } from "react-icons/tb";
import { BiImport } from "react-icons/bi";
import ImportAssignmentFromSemester from '../../component/ImportAssignmentFromSemester';

const DEFAULT_ASSIGNMENT_COUNT = 14;

const SCROLL_AMOUNT = 500;

const categorySearchBarHeights = {
    small: '24px',
    medium: '24px',
    large: '32px'
};

const matrixSizeClassNames = {
    small: 'table-compact-2',
    medium: 'table-compact',
    large: ''
};

/**
 * One ordering for the grid - subject, then topic, then category. Missing
 * names count as empty: a category that reached the grid without one used to
 * throw here, and a row is better out of place than not drawn at all.
 */
const compareMatrixCategories = (catA, catB) => {
    const subjectComparison = (catA.subject_name || "").localeCompare(catB.subject_name || "");
    if (subjectComparison !== 0) return subjectComparison;

    const topicComparison = (catA.topic_name || "").localeCompare(catB.topic_name || "");
    if (topicComparison !== 0) return topicComparison;

    return (catA.category_name || "").localeCompare(catB.category_name || "");
};

/**
 * Adds the rows the grid does not already hold, keyed on subject_topic_category.
 *
 * Always applied to the current grid rather than to a copy taken earlier: the
 * category reads and the assignment rebuild land in any order, and the one that
 * finishes second must not undo the first.
 */
const mergeMatrixCategories = (current, incoming) => {
    let merged = [...current];
    incoming.forEach(cat => {
        if (!merged.some(m => m.key === cat.key)) {
            merged.push(cat);
        }
    });
    merged.sort(compareMatrixCategories);
    return merged;
};


const AssignmentMatrixLibraryMatrix = () => {

    const appProperties = AppProperties();

    // URL params
    const [librarySubjectID, setLibrarySubjectID] = useState(null);
    const [librarySubjectName, setLibrarySubjectName] = useState("");
    const [libraryTopicID, setLibraryTopicID] = useState(null);
    const [libraryTopicName, setLibraryTopicName] = useState("");
    const [libraryMatrixID, setLibraryMatrixID] = useState(null);
    const [matrixName, setMatrixName] = useState("");

    // null until the ownership of this matrix is known. Treated as read-only
    // while unknown so no write control is live before the answer arrives.
    const [receivedFromSource, setReceivedFromSource] = useState(null);
    const isReadOnly = receivedFromSource !== false;

    // Matrix state
    const [loading, setLoading] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [allAssignmentDetails, setAllAssignmentDetails] = useState([]);
    const [matrixCategories, setMatrixCategories] = useState([]);
    const [assignmentDetails, setAssignmentDetails] = useState([]);
    const [searchText, setSearchText] = useState("");

    // Modals
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showAssignmentDetailModal, setShowAssignmentDetailModal] = useState(false);
    const [showNewAssignmentModal, setShowNewAssignmentModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [pickedAssignment, setPickedAssignment] = useState(null);
    const [pickedCategory, setPickedCategory] = useState(null);

    // Categories refused by the sync gate on the last add, listed for the user.
    // null while there is nothing to show; the dialog opens on any array.
    const [unsyncedCategories, setUnsyncedCategories] = useState(null);
    const [deletedCategories, setDeletedCategories] = useState(null);
    const [addingCategories, setAddingCategories] = useState(false);

    // Subject/Topic/Category select
    const [subjects, setSubjects] = useState(null);
    const [topics, setTopics] = useState(null);
    const [categories, setCategories] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState(null);

    // ui
    const [matrixSize, setMatrixSize] = useState("medium");

    // Delete assignment
    const [showDeleteAssignment, setShowDeleteAssignment] = useState(false);

    // Scroll arrows
    const [showArrow, setShowArrow] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        let subjectIdStr = queryParams.get("subject_id");
        let subjectNameStr = queryParams.get("subject_name");
        let topicIdStr = queryParams.get("topic_id");
        let topicNameStr = queryParams.get("topic_name");
        let matrixIdStr = queryParams.get("id");
        let matrixNameStr = queryParams.get("name");

        if (subjectNameStr) setLibrarySubjectName(decodeURIComponent(subjectNameStr));
        if (topicNameStr) setLibraryTopicName(decodeURIComponent(topicNameStr));
        if (matrixNameStr) setMatrixName(decodeURIComponent(matrixNameStr));
        if (subjectIdStr) setLibrarySubjectID(parseInt(subjectIdStr, 10));
        if (topicIdStr) setLibraryTopicID(parseInt(topicIdStr, 10));
        if (matrixIdStr) {
            let mId = parseInt(matrixIdStr, 10);
            setLibraryMatrixID(mId);
            resolveMatrixOwnership(parseInt(subjectIdStr, 10), parseInt(topicIdStr, 10), mId);
            getLibraryMatrixCategories(parseInt(subjectIdStr, 10), parseInt(topicIdStr, 10), mId);
            getLibraryMatrixAssignments(parseInt(subjectIdStr, 10), parseInt(topicIdStr, 10), mId);
        }
    }, []);

    const checkScroll = () => {
        const table = document.querySelector(".assignment-table");
        if (table) {
            setShowArrow(table.scrollWidth > table.clientWidth);
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener("resize", checkScroll);
        return () => window.removeEventListener("resize", checkScroll);
    }, [assignmentDetails]);

    useEffect(() => {
        getSubjects();
    }, []);

    const mappedCategories = useMemo(() => {
        return categories?.map(category => ({
            id: category.category_id,
            label: category.category_name,
            data: category,
        })) || [];
    }, [categories]);

    const getSubjects = () => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubjects'
        }).then(res => {
            if (res && res.data && res.data.data) {
                let subjectsArr = res.data.data.subject_names.filter(subj => subj.sync_validation);
                setSubjects(subjectsArr);
            }
        })
    }

    const getTopics = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllTopics',
            data: payload
        }).then(res => {
            if (res && res.data && res.data.data) {
                setTopics(res.data.data.topic_names);
            }
        })
    }

    const getCategories = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllCategories',
            data: payload
        }).then(res => {
            if (res && res.data && res.data.data) {
                setCategories(res.data.data.category_names);
            }
        })
    }

    const handleSubjectSelect = (_, value) => {
        setTopics(null);
        setCategories(null);
        setSelectedSubject(null);
        setSelectedTopic(null);
        setSelectedCategories(null);
        if (value == null) return;
        setSelectedSubject(value);
        getTopics({
            subject_id: value.id
        })
    }

    const handleTopicSelect = (_, value) => {
        setCategories(null);
        setSelectedTopic(null);
        setSelectedCategories(null);
        if (value == null) return;
        setSelectedTopic(value);
        getCategories({
            subject_id: value.data.subject_id,
            topic_id: value.data.topic_id
        })
    }

    const handleCategoriesSelect = (_, values) => {
        setSelectedCategories(null);
        if (values == null) return;
        let newSelectedCategories = [...values];
        setSelectedCategories(newSelectedCategories);
    }

    /**
     * Adds the picked categories to the matrix, once the server has accepted
     * them.
     *
     * The grid is not touched until the response arrives. On a matrix that is
     * already syncing, a category the other universities do not have is refused
     * - it would block every later push of this matrix - and showing it in the
     * grid first would leave a column standing for a row that was never
     * written.
     */
    const handleAddCategories = () => {
        if (isReadOnly) return;
        if (!selectedCategories || !selectedCategories.length) return;

        let addedCategories = [];
        let newCategoriesForAPI = [];

        selectedCategories.forEach(selectedCat => {
            let matrixCat = {
                ...selectedCat.data,
                subject_name: selectedSubject?.data.subject_name,
                subject_color: selectedSubject?.data.subject_color,
                subject_short: selectedSubject?.data.subject_short,
                topic_name: selectedTopic?.data.topic_name,
                topic_short: selectedTopic?.data.topic_short,
                topic_color: selectedTopic?.data.topic_color,
            };

            matrixCat.key = `${matrixCat.subject_id}_${matrixCat.topic_id}_${matrixCat.category_id}`;
            matrixCat.columnDataArr = assignmentDetails.map(_ => null);

            const alreadyExists = matrixCategories.some(cat => cat.key === matrixCat.key)
                || addedCategories.some(cat => cat.key === matrixCat.key);
            if (!alreadyExists) {
                addedCategories.push(matrixCat);
                newCategoriesForAPI.push({
                    aml_subject_id: librarySubjectID,
                    aml_topic_id: libraryTopicID,
                    aml_matrix_id: libraryMatrixID,
                    subject_id: selectedCat.data.subject_id,
                    topic_id: selectedCat.data.topic_id,
                    category_id: selectedCat.data.category_id,
                });
            }
        });

        // Everything picked is already on the matrix - nothing to write. Say so:
        // emptying the box and doing nothing else read as an add that failed.
        if (newCategoriesForAPI.length === 0) {
            NotificationManager.info(selectedCategories.length > 1
                ? "Those categories are already on this matrix"
                : "That category is already on this matrix");
            setSelectedCategories([]);
            return;
        }

        setAddingCategories(true);
        ApiRequestService({
            method: 'post',
            url: '/addAMLMatrixCategories',
            data: newCategoriesForAPI,
        }).then(res => {
            if (!res || !res.data || res.data.status !== 0) {
                NotificationManager.error(
                    (res && res.data && res.data.message) || "Could not add the categories to the matrix",
                    "ERROR", SYNC_NOTIFICATION_MS);
                return;
            }

            const validation = res.data.data;
            if (validation && validation.valid === false) {
                // Refused: the question bank has not caught up yet. The picked
                // categories are left in the box so they can be added again
                // once the question bank sync has run.
                setUnsyncedCategories(validation.unsynced_categories || []);
                return;
            }

            setMatrixCategories(current => mergeMatrixCategories(current, addedCategories));
            setSelectedCategories([]);
            NotificationManager.success("Categories saved to library matrix");
        }).catch(err => {
            console.error("Error saving library matrix categories:", err);
            NotificationManager.error("Could not add the categories to the matrix", "ERROR", SYNC_NOTIFICATION_MS);
        }).finally(() => {
            setAddingCategories(false);
        });
    };

    /**
     * Whether this matrix arrived from another university, which is the one
     * thing the page needs and the one thing its URL cannot tell it.
     *
     * Asked of the matrix listing rather than the sync status endpoint: the
     * sync routes sit behind the superadmin gate, and ownership is not a
     * superadmin question — everyone who can open the page has to get an
     * answer, or the page would lock itself for the people it is meant for.
     *
     * If no answer can be had the page stays read-only: a write that should
     * have been refused is worse than a control the user has to come back for.
     */
    const resolveMatrixOwnership = (subjectID, topicID, matrixID) => {
        ApiRequestService({
            method: 'post',
            url: '/getAMLMatrices',
            data: {
                aml_subject_id: subjectID,
                aml_topic_id: topicID,
            }
        }).then(res => {
            const rows = (res && res.data && res.data.data) || [];
            const matrix = rows.find(row => row.id === matrixID);
            setReceivedFromSource(matrix ? !!matrix.is_synced : true);
        }).catch(() => {
            setReceivedFromSource(true);
        });
    };

    // A matrix reached from a stale link can belong to a university that has
    // since switched sync off. Send the user back to the library rather than
    // leaving them on an empty grid they cannot fill.
    const leaveUnsharedMatrix = () => {
        NotificationManager.warning(
            "The university that owns this matrix has stopped sharing it.",
            "NOT AVAILABLE", SYNC_NOTIFICATION_MS);
        browserHistory.push(`${appProperties.baseFolder}AssignmentMatrixLibrary`);
    };

    const getLibraryMatrixCategories = (subjectID, topicID, matrixID) => {
        ApiRequestService({
            method: 'post',
            url: '/getAMLMatrixCategories',
            data: {
                aml_subject_id: subjectID,
                aml_topic_id: topicID,
                aml_matrix_id: matrixID,
            }
        }).then(res => {
            if (isAMLNotShared(res)) {
                leaveUnsharedMatrix();
                return;
            }
            if (res?.data?.data) {
                let categories = res.data.data.map(cat => ({
                    subject_id: cat.subject_id,
                    topic_id: cat.topic_id,
                    category_id: cat.category_id,
                    subject_name: cat.subject_name || "",
                    topic_name: cat.topic_name || "",
                    category_name: cat.category_name || "",
                    subject_color: cat.subject_color,
                    topic_color: cat.topic_color,
                    subject_short: cat.subject_short,
                    topic_short: cat.topic_short,
                    key: `${cat.subject_id}_${cat.topic_id}_${cat.category_id}`,
                    columnDataArr: [],
                }));
                setMatrixCategories(prev => mergeMatrixCategories(prev, categories));
            }
        }).catch(err => {
            console.error("Error fetching library matrix categories:", err);
        });
    };

    const getLibraryMatrixAssignments = async (subjectID, topicID, matrixID) => {
        try {
            setLoading(true);
            const res = await ApiRequestService({
                method: 'post',
                url: '/getAMLMatrixAssignments',
                data: {
                    aml_subject_id: subjectID,
                    aml_topic_id: topicID,
                    aml_matrix_id: matrixID,
                }
            });

            if (res?.data?.data?.quizes_list) {
                let quizArr = [];
                let quizIDArr = [];
                res.data.data.quizes_list.forEach(quiz => {
                    if (!quizIDArr.includes(quiz.quiz_id)) {
                        quizIDArr.push(quiz.quiz_id);
                        quizArr.push(quiz);
                    }
                });

                setAssignments(quizArr);
                getAssignmentDetails({ quiz_id_arr: quizIDArr });
            } else {
                setLoading(false);
                setAssignments([]);
                setAssignmentDetails([]);
            }
        } catch (err) {
            setLoading(false);
            console.error("Error fetching library matrix assignments:", err);
        }
    };


    const getAssignmentDetails = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getQuizzesByIds',
            data: payload
        }).then(res => {
            if (res && res.data && res.data.data) {
                let arr = res.data.data;
                setAllAssignmentDetails(arr);
                rebuildMatrixFromAssignments(arr);
            }
        }).finally(() => {
            setLoading(false);
        })
    }

    const handleAddAssignmentClick = () => {
        if (isReadOnly) return;
        setShowNewAssignmentModal(true);
    };

    // Imported assignments may bring categories the matrix did not have, so
    // both the rows and the columns are reloaded rather than patched in.
    const onCompleteImportAssignments = () => {
        setShowImportModal(false);
        getLibraryMatrixCategories(librarySubjectID, libraryTopicID, libraryMatrixID);
        getLibraryMatrixAssignments(librarySubjectID, libraryTopicID, libraryMatrixID);
    };

    const onCompleteCreateAssignment = (newAssignment) => {
        let updatedAssignments = [...assignmentDetails, newAssignment];
        setAssignmentDetails(updatedAssignments);

        // Update matrixCategories columnDataArr
        let updatedCategories = matrixCategories.map(cat => ({
            ...cat,
            columnDataArr: [...(cat.columnDataArr || []), {
                quiz_id: newAssignment.quiz_id,
                quiz_type: newAssignment.quiz_type,
            }]
        }));
        setMatrixCategories(updatedCategories);
    };

    const handleCategoryCellClick = (cat, columnDataIndex) => {
        let assignmentData = assignmentDetails[columnDataIndex];
        if (assignmentData && cat.key) {
            setPickedAssignment(assignmentData);
            let obj = cat.columnDataArr[columnDataIndex];
            setPickedCategory({
                ...cat,
                key: `${cat.subject_id}_${cat.topic_id}_${cat.category_id}`,
                ...obj,
                columnDataArr: null,
            });
            setShowCategoryModal(true);
        }
    };

    const handleAssignmentHeaderClick = (assignment) => {
        setSelectedAssignment({ ...assignment });
        setShowAssignmentDetailModal(true);
    };

    const onSaveCategoryModal = (updatedAssignment) => {
        let index = assignmentDetails.findIndex(assign => assign.quiz_id === updatedAssignment.quiz_id);
        if (index > -1) {
            let updatedAssignmentDetails = [...assignmentDetails];
            updatedAssignmentDetails[index] = { ...updatedAssignment };
            rebuildMatrixFromAssignments(updatedAssignmentDetails);
            setShowCategoryModal(false);
        }
    };

    const onSaveAssignment = (updatedAssignment) => {
        let index = assignmentDetails.findIndex(assign => assign.quiz_id === updatedAssignment.quiz_id);
        if (index > -1) {
            let updatedAssignmentDetails = [...assignmentDetails];
            updatedAssignmentDetails[index] = { ...updatedAssignment };
            rebuildMatrixFromAssignments(updatedAssignmentDetails);
        }
    };

    const onDeleteAssignment = () => {
        if (isReadOnly || !selectedAssignment) return;
        if (!hasValidLibraryIds(selectedAssignment.quiz_id)) {
            NotificationManager.error("Could not delete the assignment: missing quiz id", "ERROR", SYNC_NOTIFICATION_MS);
            return;
        }
        // /deleteAMLMatrix deletes a matrix, and keys on the subject/topic/matrix
        // triple - a bare quiz id matched nothing there and reported success for
        // it. Assignments have their own endpoint, which soft deletes and queues
        // the push so the removal reaches every target.
        ApiRequestService({
            method: 'post',
            url: '/deleteAMLAssignment',
            data: {
                quiz_id: selectedAssignment.quiz_id,
            }
        }).then(res => {
            // Decides on the status, not on the call returning - see
            // handleDeleteCategory below.
            if (!res || !res.data || res.data.status !== 0) {
                NotificationManager.error(
                    (res && res.data && res.data.message) || "Could not delete the assignment",
                    "ERROR", SYNC_NOTIFICATION_MS);
                return;
            }

            // The column index has to be read before the assignment is dropped:
            // every category's columnDataArr is positional against this list.
            let deletedIndex = assignmentDetails.findIndex(a => a.quiz_id === selectedAssignment.quiz_id);
            setAssignmentDetails(assignmentDetails.filter(a => a.quiz_id !== selectedAssignment.quiz_id));

            if (deletedIndex > -1) {
                setMatrixCategories(current => current.map(cat => {
                    let newArr = [...(cat.columnDataArr || [])];
                    newArr.splice(deletedIndex, 1);
                    return { ...cat, columnDataArr: newArr };
                }));
            }

            setShowDeleteAssignment(false);
            setSelectedAssignment(null);
            NotificationManager.success("Assignment removed from matrix");
        }).catch(err => {
            console.error("Error deleting library assignment:", err);
            NotificationManager.error("Could not delete the assignment", "ERROR", SYNC_NOTIFICATION_MS);
        })
    };

    const handleDeleteCategory = (cat) => {
        if (isReadOnly) return;
        if (!hasValidLibraryIds(librarySubjectID, libraryTopicID, libraryMatrixID,
            cat?.subject_id, cat?.topic_id, cat?.category_id)) {
            NotificationManager.error("Could not remove the category: missing library or category id",
                "ERROR", SYNC_NOTIFICATION_MS);
            return;
        }

        // The grid is not touched until the server answers, the same way
        // handleAddCategories waits. Dropping the row first made a refused
        // delete look like it had worked until a reload put the row back.
        ApiRequestService({
            method: 'post',
            url: '/deleteAMLMatrixCategory',
            data: {
                aml_subject_id: librarySubjectID,
                aml_topic_id: libraryTopicID,
                aml_matrix_id: libraryMatrixID,
                subject_id: cat.subject_id,
                topic_id: cat.topic_id,
                category_id: cat.category_id,
            }
        }).then(res => {
            if (!res || !res.data || res.data.status !== 0) {
                NotificationManager.error(
                    (res && res.data && res.data.message) || "Could not remove the category from the matrix",
                    "ERROR", SYNC_NOTIFICATION_MS);
                return;
            }

            setMatrixCategories(current => current.filter(mc => mc.key !== cat.key));
            NotificationManager.success("Category removed from matrix");
        }).catch(err => {
            console.error("Error deleting library matrix category:", err);
            NotificationManager.error("Could not remove the category from the matrix",
                "ERROR", SYNC_NOTIFICATION_MS);
        });
    };

    const rebuildMatrixFromAssignments = (updatedAssignmentDetails) => {
        setMatrixCategories(prev => {
            let updatedMatrixCategories = [...prev];
            let catKeysSet = new Set();

            updatedMatrixCategories.forEach(matrixCat => {
                let catKey = `${matrixCat.subject_id}_${matrixCat.topic_id}_${matrixCat.category_id}`;
                catKeysSet.add(catKey);
            });

            // Add any new categories from assignments
            updatedAssignmentDetails.forEach(assignDetail => {
                if (assignDetail.quiz_subcat_info) {
                    assignDetail.quiz_subcat_info.forEach(detail => {
                        let catKey = `${detail.subject_id}_${detail.topic_id}_${detail.category_id}`;
                        if (!catKeysSet.has(catKey)) {
                            catKeysSet.add(catKey);
                            updatedMatrixCategories.push({
                                subject_name: detail.subject_name || "",
                                topic_name: detail.topic_name || "",
                                category_name: detail.category_Name || detail.category_name || "",
                                key: catKey,
                                subject_id: detail.subject_id,
                                topic_id: detail.topic_id,
                                category_id: detail.category_id,
                                subject_color: detail.subject_color,
                                topic_color: detail.topic_color,
                                subject_short: detail.subject_short_form,
                                topic_short: detail.topic_short_form,
                                columnDataArr: [],
                            });
                        }
                    });
                }
            });

            // Rebuild columnDataArr for each category
            updatedMatrixCategories.forEach((matrixCategory, index) => {
                let columnDataArr = [];
                updatedAssignmentDetails.forEach(assignDetail => {
                    let categoryMasteryCountJSON = {};
                    let categoryIsRequiredJSON = {};
                    let categoryPositionJSON = {};
                    let categoryNoOfQuestionsJSON = {};
                    try {
                        categoryMasteryCountJSON = JSON.parse(assignDetail.category_mastery_count || '{}');
                        categoryIsRequiredJSON = JSON.parse(assignDetail.category_is_required || '{}');
                        categoryPositionJSON = JSON.parse(assignDetail.category_position || '{}');
                        categoryNoOfQuestionsJSON = JSON.parse(assignDetail.category_number_of_questions || '{}');
                    } catch (e) {
                        console.error('JSON parse error:', e);
                    }

                    if (assignDetail.quiz_subcat_info) {
                        let category = assignDetail.quiz_subcat_info.find(cat => {
                            let key = `${cat.subject_id}_${cat.topic_id}_${cat.category_id}`;
                            return key === matrixCategory.key;
                        });
                        if (category) {
                            let mc = categoryMasteryCountJSON?.[matrixCategory.key] ?? null;
                            let noOfQuestions = categoryNoOfQuestionsJSON[matrixCategory.key] ?? null;
                            columnDataArr.push({
                                quiz_id: assignDetail.quiz_id,
                                quiz_name: assignDetail.quiz_name,
                                quiz_type: assignDetail.quiz_type,
                                masteryCount: mc === 0 ? DEFAULT_MASTERY_COUNT : mc,
                                noOfQuestions: noOfQuestions && noOfQuestions !== 0 ? noOfQuestions : category.question_count,
                                atFirst: categoryPositionJSON?.[matrixCategory.key]?.category_at_begining === 1 ?? null,
                                atLast: categoryPositionJSON?.[matrixCategory.key]?.category_at_last === 1 ?? null,
                                freeChoice: categoryIsRequiredJSON?.[matrixCategory.key] === 1 ?? null,
                                isTest: assignDetail.quiz_type === 1,
                                isPracticeAssignment: assignDetail.is_practice_assignment,
                            });
                        } else {
                            columnDataArr.push({
                                quiz_id: assignDetail.quiz_id,
                                quiz_type: assignDetail.quiz_type,
                            });
                        }
                    } else {
                        columnDataArr.push({
                            quiz_id: assignDetail.quiz_id,
                            quiz_type: assignDetail.quiz_type,
                        });
                    }
                });
                updatedMatrixCategories[index].columnDataArr = columnDataArr;
            });

            updatedMatrixCategories.sort(compareMatrixCategories);

            return updatedMatrixCategories;
        });
        setAssignmentDetails(updatedAssignmentDetails);
    };

    const handleLeftArrowClick = () => {
        let table = document.querySelector(".assignment-table");
        if (table) {
            table.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
        }
    };

    const handleRightArrowClick = () => {
        let table = document.querySelector(".assignment-table");
        if (table) {
            table.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
        }
    };

    return (
        <div className='assign-lib-cont'>
            {/* Breadcrumb */}
            <div className='assign-lib-breadcrumb' style={{
                display: "flex",
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '1rem'
            }}>
                <div>
                    <div>
                        <span
                            className='assign-lib-breadcrumb-link'
                            onClick={() => {
                                browserHistory.push(`${appProperties.baseFolder}AssignmentMatrixLibrary`);
                            }}
                        >
                            Assignment Matrix Library
                        </span>
                        <span className='assign-lib-breadcrumb-separator'>/</span>
                        <span
                            className='assign-lib-breadcrumb-link'
                            onClick={() => {
                                browserHistory.push(`${appProperties.baseFolder}AssignmentMatrixLibrarySubject?id=${librarySubjectID}&name=${encodeURIComponent(librarySubjectName)}`);
                            }}
                        >
                            {librarySubjectName || "Subject"}
                        </span>
                        <span className='assign-lib-breadcrumb-separator'>/</span>
                        <span
                            className='assign-lib-breadcrumb-link'
                            onClick={() => {
                                browserHistory.push(`${appProperties.baseFolder}AssignmentMatrixLibraryTopic?subject_id=${librarySubjectID}&subject_name=${encodeURIComponent(librarySubjectName)}&topic_id=${libraryTopicID}&topic_name=${encodeURIComponent(libraryTopicName)}`);
                            }}
                        >
                            {libraryTopicName || "Topic"}
                        </span>
                        <span className='assign-lib-breadcrumb-separator'>/</span>
                        <span className='assign-lib-breadcrumb-current'>
                            {matrixName || "Matrix"}
                        </span>
                    </div>
                    <Typography fontWeight={500} fontSize={24}>
                        {matrixName || "Matrix"}
                    </Typography>
                    {receivedFromSource ?
                        <div className='matrix-read-only-note'>
                            Read only &mdash; {READ_ONLY_HINT}
                        </div>
                        : null}
                </div>

                <Box component="fieldset" className='legends' style={{ width: "600px" }}>
                    <legend>Legend</legend>
                    <SFTooltip text="Assignment setup is incomplete. Please complete it to publish.">
                        <div className='single-legend'>
                            <FaExclamation color='red' fontSize={20} />
                            <div> - Missing data</div>
                        </div>
                    </SFTooltip>
                    <SFTooltip text={<div>
                        <div>Mastery - Assignment in mastery mode in which student must correctly answer N = Mastery Count questions in a row to get credit for the skill category. <br /> For partially completed categories, partial credit is allowed only if the “Allow Partial Credit” is selected in the assignment settings.</div>
                        <br />
                        <div>Level - Required number of correctly-answered questions in a row to get full credit for the assigned skill category.</div>
                    </div>}>
                        <div className='single-legend'>
                            <div className='mastery-box'>#</div>
                            <div> - Mastery, Level</div>
                        </div>
                    </SFTooltip>
                    {/* <SFTooltip text="Required number of correctly-answered questions in a row to get full credit for the assigned skill category.">
                                                <div className='single-legend'>
                                                    <FaHashtag color='#bb0000' fontSize={16} />
                                                    <div> - Mastery Count</div>
                                                </div>
                                            </SFTooltip> */}
                    <SFTooltip text={
                        <div>
                            <div>Test - Assignment in test mode in which students answer questions, like in a test or quiz.</div>
                            <br />
                            <div>Questions - Number of questions</div>
                        </div>
                    }>
                        <div className='single-legend'>
                            <div className='test-box'>#</div>
                            <div> - Test, Questions</div>
                        </div>
                    </SFTooltip>
                    <SFTooltip text="The category in the assignment is viewed first by the student and shown until mastery is achieved.">
                        <div className='single-legend'>
                            <div class="triangle" style={{ borderLeftColor: "#34C759" }}></div>
                            <div> - At First</div>
                        </div>
                    </SFTooltip>
                    <SFTooltip text="The category is viewed last by the student, after mastery is achieved by all other categories in the assignment.">
                        <div className='single-legend'>
                            <div class="triangle" style={{ borderLeftColor: "#FF3B30" }}></div>
                            <div> - At Last</div>
                        </div>
                    </SFTooltip>
                    <SFTooltip text="The category is not strictly required: it is in a list of N categories from which students must choose and master at least n<N categories to achieve full credit for the assignment.">
                        <div className='single-legend'>
                            <div class="triangle" style={{ borderLeftColor: "#FF9500" }}></div>
                            <div> - Free Choice</div>
                        </div>
                    </SFTooltip>
                    <SFTooltip text="Indicates that the topics in this practice assignment are available in the student's Topics tab for building their own practice assignments">
                        <div className='single-legend'>
                            <TbTargetArrow color='#ec16d8' fontSize={20} />
                            <div> - Practice Assignment</div>
                        </div>
                    </SFTooltip>
                </Box>
            </div>

            {/* Subject/Topic/Category Selection */}
            <div style={{ width: '100%', margin: '1rem 0rem', display: "flex", justifyContent: "space-between", }}>
                <Grid container spacing={2} alignItems={'start'} flexWrap={'wrap'}>
                    <React.Fragment>
                        <Grid item xs={2}>
                            <Autocomplete
                                disablePortal
                                size='small'
                                options={subjects ? subjects.map(subject => {
                                    return {
                                        id: subject.subject_id,
                                        label: subject.subject_name,
                                        data: subject,
                                    }
                                }) : []}
                                value={selectedSubject}
                                onChange={handleSubjectSelect}
                                disabled={isReadOnly}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Subject"
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: {
                                                borderRadius: "6px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Tooltip
                                title={!selectedSubject ?
                                    <Typography className="text-white" fontSize={14}>
                                        Please select a subject
                                    </Typography>
                                    : ""}
                                arrow
                                placement='top'
                                disableHoverListener={!!selectedSubject}
                            >
                                <span>
                                    <Autocomplete
                                        disablePortal
                                        size='small'
                                        options={topics ? topics.map(topic => {
                                            return {
                                                id: topic.topic_id,
                                                label: topic.topic_name,
                                                data: topic,
                                            }
                                        }) : []}
                                        onChange={handleTopicSelect}
                                        value={selectedTopic}
                                        disabled={isReadOnly || !selectedSubject}
                                        getOptionLabel={(option) => option.label}
                                        sx={{
                                            backgroundColor: !selectedSubject ? "#f5f5f5" : "white",
                                            opacity: !selectedSubject ? 0.6 : 1,
                                            "& .MuiInputBase-root": {
                                                cursor: !selectedSubject ? "not-allowed" : "pointer",
                                            },
                                            "& .MuiInputBase-input": {
                                                cursor: !selectedSubject ? "not-allowed" : "pointer",
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Topic"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    sx: {
                                                        borderRadius: "6px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={2}>
                            <Tooltip
                                title={!selectedTopic ?
                                    <Typography className="text-white" fontSize={14}>
                                        Please select a topic
                                    </Typography>
                                    : ""}
                                arrow
                                placement='top'
                                disableHoverListener={!!selectedTopic}
                            >
                                <span>
                                    <Autocomplete
                                        disablePortal
                                        size='small'
                                        options={mappedCategories}
                                        multiple
                                        filterSelectedOptions
                                        disabled={isReadOnly || !selectedTopic}
                                        value={selectedCategories ? selectedCategories : []}
                                        onChange={handleCategoriesSelect}
                                        getOptionLabel={(option) => option.label}
                                        sx={{
                                            backgroundColor: !selectedTopic ? "#f5f5f5" : "white",
                                            opacity: !selectedTopic ? 0.6 : 1,
                                            "& .MuiInputBase-root": {
                                                cursor: !selectedTopic ? "not-allowed" : "pointer",
                                            },
                                            "& .MuiInputBase-input": {
                                                cursor: !selectedTopic ? "not-allowed" : "pointer",
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Categories"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    sx: {
                                                        borderRadius: "6px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </span>
                            </Tooltip>

                        </Grid>
                    </React.Fragment>
                    <Grid item xs={4}>
                        <div style={{ display: 'flex', justifyContent: "start", alignItems: "center", gap: "12px" }}>
                            {isReadOnly ? null :
                                <Tooltip
                                    arrow
                                    disableHoverListener={selectedCategories && selectedCategories.length > 0}
                                    title={
                                        <Typography className="text-white" fontSize={14}>
                                            Please select one or more categories to add to the matrix.
                                        </Typography>
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            onClick={handleAddCategories}
                                            disabled={addingCategories || !selectedCategories || selectedCategories.length === 0}
                                        >
                                            {`Add Categor${selectedCategories && selectedCategories.length > 1 ? 'ies' : 'y'} To Matrix`}
                                        </Button>
                                    </span>
                                </Tooltip>}
                            {isReadOnly ? null :
                                <Button variant='contained' onClick={() => setShowImportModal(true)}>
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: '6px' }}>
                                        <BiImport fontSize={20} />
                                        <div>Import Assignments</div>
                                    </div>
                                </Button>}
                            <DownloadMatrixCSV
                                assignmentDetails={assignmentDetails}
                                fileName={matrixName || "matrix"}
                                matrixCategories={matrixCategories}
                            />
                        </div>
                    </Grid>
                </Grid>
            </div>

            {/* Matrix Table */}
            <div className='table-parent' style={{ marginTop: '1rem' }}>
                {showArrow ?
                    <React.Fragment>
                        <div className='matrix-arrow left-arrow'>
                            <IconButton onClick={handleLeftArrowClick}>
                                <FaArrowLeft fontSize={16} color='#fff' />
                            </IconButton>
                        </div>
                        <div className='matrix-arrow right-arrow'>
                            <IconButton onClick={handleRightArrowClick}>
                                <FaArrowRight fontSize={16} color='#fff' />
                            </IconButton>
                        </div>
                    </React.Fragment> : null}

                <div className='matrix-table-container assignment-table'>
                    <table className={`matrix-table ${matrixSizeClassNames[matrixSize]}`}>
                        <thead className='matrix-table-head'>
                            <tr className='cat-assign-header'>
                                <th colSpan={2}>
                                    <div className='category-header'>
                                        <div>Categories</div>
                                        <small>({matrixCategories?.length})</small>
                                    </div>
                                    <div className='compact-container'>
                                        <FormControl>
                                            <RadioGroup
                                                row
                                                onChange={(_, val) => {
                                                    setMatrixSize(val);
                                                }}
                                                value={matrixSize}
                                                aria-labelledby="demo-row-radio-buttons-group-label"
                                                name="row-radio-buttons-group"
                                            >
                                                <FormControlLabel value="small" control={<Radio size='small' />} label="Small" />
                                                <FormControlLabel value="medium" control={<Radio size='small' />} label="Medium" />
                                                <FormControlLabel value="large" control={<Radio size='small' />} label="Large" />
                                            </RadioGroup>
                                        </FormControl>
                                    </div>
                                </th>
                                <th colSpan={assignmentDetails?.length + 10 ?? 0}>
                                    <div className='assignment-header'>
                                        <div>Assignments
                                            <small>({assignmentDetails?.length})</small>
                                        </div>
                                    </div>
                                </th>
                            </tr>
                            <tr className="matrix-row-header">
                                <th className="matrix-row-cell">
                                    <div className='no-cell-top-border'></div>
                                    <div style={{ width: "100%" }}>No.</div>
                                </th>
                                <th className="matrix-row-cell" style={{ minWidth: '400px' }}>
                                    <div className='category-cell-top-border'></div>
                                    <div style={{ width: '100%' }}>
                                        <TextField
                                            size='small'
                                            className='search-category-field'
                                            placeholder='Search Categories'
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <IoIosSearch fontSize={24} />
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                width: '90%',
                                                borderRadius: '8px',
                                                '& .MuiInputBase-root': {
                                                    height: '24px',
                                                }
                                            }}
                                        />
                                    </div>
                                </th>
                                {assignmentDetails?.map((assign, assignIndex) => (
                                    <th
                                        key={assignIndex}
                                        className="matrix-row-cell"
                                    >
                                        <div
                                            className="assign-header-tag cat-cell"
                                            onClick={() => handleAssignmentHeaderClick(assign)}
                                            style={{ cursor: 'pointer', color: 'black' }}
                                        >
                                            <div className='assign-cell-top-border'></div>
                                            <div className="cat-cell-border"></div>
                                            {assignIndex + 1}
                                        </div>
                                    </th>
                                ))}
                                <th className="matrix-row-cell add-cell-container">
                                    {isReadOnly ? null :
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            style={{ width: "100%", height: "100%", borderRadius: "0" }}
                                            onClick={handleAddAssignmentClick}
                                        >
                                            + ADD
                                        </Button>}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {matrixCategories
                                .filter(cat => cat.category_name.toLowerCase().includes(searchText.toLowerCase()))
                                .map((cat, index) => {
                                    let showDeleteBtn = true;
                                    if (cat.columnDataArr) {
                                        for (const catColData of cat.columnDataArr) {
                                            if (catColData !== null && catColData !== undefined && catColData.masteryCount !== undefined) {
                                                showDeleteBtn = false;
                                                break;
                                            }
                                        }
                                    }

                                    return (
                                        <tr className="matrix-row" key={index}>
                                            <td className="matrix-row-cell" style={{
                                                backgroundColor: '#fafafa',
                                            }}>{index + 1}</td>
                                            <td className="matrix-row-cell category-width" style={{
                                                textAlign: 'left',
                                                backgroundColor: '#fafafa',
                                            }}>
                                                <div className='category-name-cell'>
                                                    <div style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        width: "100%",
                                                    }}>
                                                        <div style={{
                                                            display: "flex",
                                                            justifyContent: "flex-start",
                                                            alignItems: "center",
                                                        }}>
                                                            <div className='stackable-icon-container'>
                                                                <Tooltip
                                                                    title={<Typography className='text-white' fontSize={14}>
                                                                        Subject: {cat.subject_name}
                                                                    </Typography>}
                                                                    arrow placement="top">
                                                                    <div className='stackable-icon stackable-subject-icon' style={{
                                                                        backgroundColor: cat?.subject_color || getRandomColorForSubjectMatrix(),
                                                                        borderColor: 'transparent',
                                                                        color: "#fff",
                                                                    }}>
                                                                        {cat.subject_short || (cat.subject_name ? generateLabelForName(cat.subject_name) : "")}
                                                                    </div>
                                                                </Tooltip>
                                                                <Tooltip
                                                                    title={<Typography className='text-white' fontSize={14}>
                                                                        Topic: {cat.topic_name}
                                                                    </Typography>}
                                                                    arrow placement="top">
                                                                    <div className='stackable-icon stackable-topic-icon' style={{
                                                                        backgroundColor: cat?.topic_color || getRandomColorForTopicMatrix(),
                                                                        borderColor: 'transparent',
                                                                        color: "#fff",
                                                                    }}>
                                                                        {cat.topic_short || (cat.topic_name ? generateLabelForName(cat.topic_name) : "")}
                                                                    </div>
                                                                </Tooltip>
                                                            </div>
                                                            <div className='category-name'>
                                                                {cat.category_name}
                                                            </div>
                                                        </div>
                                                        {showDeleteBtn && !isReadOnly ?
                                                            <Tooltip title="Remove category from matrix" arrow placement='top'>
                                                                <IconButton size='small' onClick={() => handleDeleteCategory(cat)}>
                                                                    <GoTrash color='#bb0000' fontSize={14} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            : null}
                                                    </div>
                                                </div>
                                            </td>

                                            {cat.columnDataArr && cat.columnDataArr.length ?
                                                cat.columnDataArr.map((columnData, columnDataIndex) => {
                                                    let backgroundColor = '';
                                                    let triangleColor = "transparent";

                                                    if (columnData) {
                                                        if (columnData.atFirst) {
                                                            triangleColor = "#34c759";
                                                        } else if (columnData.atLast) {
                                                            triangleColor = "#ff3b30";
                                                        } else if (columnData.freeChoice) {
                                                            triangleColor = "#ff9500";
                                                        }
                                                    }

                                                    if (columnData) {
                                                        if (columnData.isTest && columnData.noOfQuestions > 0) {
                                                            backgroundColor = 'rgba(0, 4, 255, 0.05)';
                                                        } else if (!columnData.isTest && columnData.masteryCount > 0) {
                                                            backgroundColor = 'rgba(187, 0, 0, 0.05)';
                                                        }
                                                        if (columnData.isPracticeAssignment) {
                                                            backgroundColor = "rgba(236, 22, 216, 0.1)";
                                                        }
                                                    }

                                                    let textColor = "#bb0000";
                                                    if (columnData?.isTest) {
                                                        textColor = "#0004FF";
                                                    }
                                                    if (columnData?.isPracticeAssignment) {
                                                        textColor = "#ec16d8";
                                                    }

                                                    return (
                                                        <td key={columnDataIndex} className="matrix-row-cell">
                                                            <div
                                                                className={`cat-cell ${columnData !== null ? "cat-cell-content" : ""}`}
                                                                style={{
                                                                    color: textColor,
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => handleCategoryCellClick(cat, columnDataIndex)}
                                                            >
                                                                <div className='cat-cell-border' style={{
                                                                    backgroundColor: backgroundColor,
                                                                }}></div>
                                                                <div className='cat-cell-triangle' style={{
                                                                    borderLeftColor: triangleColor,
                                                                }}></div>
                                                                <span className='main-cell-content'>
                                                                    {columnData?.isTest
                                                                        ? columnData?.noOfQuestions
                                                                        : columnData?.masteryCount
                                                                    }
                                                                </span>
                                                            </div>
                                                        </td>
                                                    );
                                                })
                                                : null}
                                            <td className="matrix-row-cell add-cell"></td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modals */}
            {isReadOnly ? null :
                <ImportAssignmentFromSemester
                    visible={showImportModal}
                    onHide={() => setShowImportModal(false)}
                    onCompleteCopyQuiz={onCompleteImportAssignments}
                    courseID={null}
                    fromMatrixPage={true}
                    toLibrary={true}
                    targetLibrarySubjectID={librarySubjectID}
                    targetLibraryTopicID={libraryTopicID}
                    targetLibraryMatrixID={libraryMatrixID}
                    targetLibraryMatrixName={matrixName}
                    onUnsyncedCategories={setUnsyncedCategories}
                    onDeletedCategories={setDeletedCategories}
                />}

            {showNewAssignmentModal ?
                <NewAssignmentModal
                    showModal={showNewAssignmentModal}
                    handleClose={() => setShowNewAssignmentModal(false)}
                    onCompleteCreateAssignment={onCompleteCreateAssignment}
                    course_id={null}
                    section_Id={null}
                    sections={[]}
                    isLibrary={true}
                    librarySubjectID={librarySubjectID}
                    libraryTopicID={libraryTopicID}
                    libraryMatrixID={libraryMatrixID}
                />
                : null}

            {showAssignmentDetailModal && selectedAssignment ?
                <AssignmentDetailModal
                    showModal={showAssignmentDetailModal}
                    assignment={selectedAssignment}
                    handleClose={() => {
                        setShowAssignmentDetailModal(false);
                        setSelectedAssignment(null);
                    }}
                    onSaveAssignment={onSaveAssignment}
                    course_id={null}
                    isLibrary={true}
                    disableButtons={isReadOnly}
                    librarySubjectID={librarySubjectID}
                    libraryTopicID={libraryTopicID}
                    onDeleteAssignment={isReadOnly ? undefined : () => {
                        setShowAssignmentDetailModal(false);
                        setShowDeleteAssignment(true);
                    }}
                />
                : null}

            {showCategoryModal && pickedAssignment && pickedCategory ?
                <CategoryModal
                    disableButtons={isReadOnly}
                    showModal={showCategoryModal}
                    handleClose={() => {
                        setShowCategoryModal(false);
                        setPickedAssignment(null);
                        setPickedCategory(null);
                    }}
                    pickedAssignment={pickedAssignment}
                    pickedCategoryKey={pickedCategory.key}
                    pickedCategory={pickedCategory}
                    onSaveCategoryModal={onSaveCategoryModal}
                />
                : null}

            <DeleteDialog
                showDialog={showDeleteAssignment}
                title="Delete Assignment"
                description={
                    <div>Are you sure you want to delete "{selectedAssignment?.quiz_name}" assignment from this matrix?</div>
                }
                onCancel={() => {
                    setShowDeleteAssignment(false);
                }}
                onDelete={() => {
                    onDeleteAssignment();
                }}
            />

            <UnsyncedCategoriesDialog
                unsyncedCategories={unsyncedCategories}
                onClose={() => setUnsyncedCategories(null)}
                title="These categories are not synced yet"
                description={"This matrix is syncing to the other universities, and the categories below are not synced there. "
                    + "They were not added: the matrix would stop syncing on the next push. "
                    + "Sync them from the question bank first, then add them here."}
            />
            <DeletedCategoriesDialog
                deletedCategories={deletedCategories}
                onClose={() => setDeletedCategories(null)}
            />
        </div>
    );
};

export default AssignmentMatrixLibraryMatrix;

